import { useEffect, useState } from "react";
import type { FilterValue } from "@/components/filter-popover";
import type { Activity } from "@/hooks/api/use-account-activities";
import type {
  CollapsedActivity,
  FilterMode,
} from "@/pages/accounts/account-explorer/touchpoints-table/types";
import { matchesDynamicFilters } from "@/pages/accounts/account-explorer/touchpoints-table/utils";

interface CTAVisibilityState {
  showAboveCTA: boolean;
  showBelowCTA: boolean;
  nearestAboveDate: string | null;
  nearestBelowDate: string | null;
}

/**
 * Custom hook to determine visibility of scroll CTAs for filtered touchpoints
 * that are outside the current visible range.
 */
export function useCTAVisibility(
  filterMode: FilterMode,
  filters: Record<string, FilterValue>,
  filteredActivities: Activity[],
  collapsedData: (Activity | CollapsedActivity)[],
  visibleDateRange: { start: string; end: string } | null,
): CTAVisibilityState {
  const [showAboveCTA, setShowAboveCTA] = useState(false);
  const [showBelowCTA, setShowBelowCTA] = useState(false);
  const [nearestAboveDate, setNearestAboveDate] = useState<string | null>(null);
  const [nearestBelowDate, setNearestBelowDate] = useState<string | null>(null);

  useEffect(() => {
    // Reset state unconditionally at the start
    setShowAboveCTA(false);
    setShowBelowCTA(false);
    setNearestAboveDate(null);
    setNearestBelowDate(null);

    // Only proceed if filters are active, range is known, and there are filtered activities
    if (
      filterMode !== "highlight" ||
      Object.keys(filters).length === 0 ||
      !visibleDateRange ||
      filteredActivities.length === 0
    ) {
      return;
    }

    // Step 1: Find nearest off-screen ACTIVITIES using the filtered list
    let foundNearestAboveActivity: Activity | null = null;
    let foundNearestBelowActivity: Activity | null = null;

    // Find nearest above ACTIVITY (latest date *before* start)
    for (const activity of filteredActivities) {
      if (activity.date < visibleDateRange.start) {
        if (
          !foundNearestAboveActivity ||
          activity.date > foundNearestAboveActivity.date
        ) {
          foundNearestAboveActivity = activity;
        }
      } else {
        break;
      }
    }

    // Find nearest below ACTIVITY (earliest date *after* end)
    for (const activity of filteredActivities) {
      if (activity.date > visibleDateRange.end) {
        foundNearestBelowActivity = activity;
        break;
      }
    }

    // Step 2: Find the corresponding RENDERED ROW dates for these activities
    let foundNearestAboveDate: string | null = null;
    if (foundNearestAboveActivity) {
      const targetId = foundNearestAboveActivity.id;
      for (const item of collapsedData) {
        if (
          ("activities" in item &&
            item.activities.some((a) => a.id === targetId)) ||
          item.id === targetId
        ) {
          foundNearestAboveDate = item.date; // Get the date of the row itself
          break;
        }
      }
    }

    let foundNearestBelowDate: string | null = null;
    if (foundNearestBelowActivity) {
      const targetId = foundNearestBelowActivity.id;
      for (const item of collapsedData) {
        if (
          ("activities" in item &&
            item.activities.some((a) => a.id === targetId)) ||
          item.id === targetId
        ) {
          foundNearestBelowDate = item.date; // Get the date of the row itself
          break;
        }
      }
    }

    // Step 3: Check if any matching activity is *already* within the visible range
    let isMatchVisible = false;
    if (visibleDateRange) {
      for (const item of collapsedData) {
        // Handle both individual activities and the dates of collapsed groups
        const itemStartDate = item.date;
        const itemEndDate = "endDate" in item ? item.endDate : item.date; // Use endDate for groups

        // Check if the item (or group) overlaps with the visible range
        const overlapsVisible =
          itemStartDate <= visibleDateRange.end &&
          itemEndDate >= visibleDateRange.start;

        if (overlapsVisible) {
          // Now check if this specific item actually matches the active filters
          // Use the same utility function the row className uses
          if (
            matchesDynamicFilters(item, filters as Record<string, FilterValue>)
          ) {
            isMatchVisible = true;
            break; // Found a visible match, no need to check further
          }
        }
      }
    }

    // Step 4: Explicitly set state only if a target ROW was found AND no match is visible
    let showAbove = false;
    let showBelow = false;
    if (foundNearestAboveDate) {
      setNearestAboveDate(foundNearestAboveDate);
      showAbove = !isMatchVisible;
      setShowAboveCTA(showAbove);
    }
    if (foundNearestBelowDate) {
      setNearestBelowDate(foundNearestBelowDate);
      showBelow = !isMatchVisible;
      setShowBelowCTA(showBelow);
    }

    setShowAboveCTA(showAbove);
    setShowBelowCTA(showBelow);
  }, [
    filteredActivities,
    visibleDateRange,
    filterMode,
    filters,
    collapsedData,
  ]);

  return {
    showAboveCTA,
    showBelowCTA,
    nearestAboveDate,
    nearestBelowDate,
  };
}
